import { useCallback } from 'react';
import { useEventTracking } from '@services/statsig';

import type { EligibilitySource } from '../types/source';
import { dueCheckReason } from '../utils/eligibilityCheckState';

import { useEligibilityBadgeState } from './useEligibilityBadgeState';

export interface PlanChangedCheck {
  /** Why a check is due, in the server's words ("Plan changed"), or null for no offer. */
  reason: string | null;
  isRunning: boolean;
  onRunCheck: () => void;
}

/**
 * The offer to check a plan again after its member ID or payer changed
 * (GDN-4267). The server decides: the offer stands while the patient's
 * eligibility read says a check is due and no check speaks for the plan. A
 * person starts the check; nothing runs on its own, because checks are billed.
 */
export function usePlanChangedCheck(
  patientId: string,
  source: EligibilitySource,
): PlanChangedCheck {
  const { readiness, readinessLabel, eligibilityCheckState, runCheck, isRunning } =
    useEligibilityBadgeState({ patientId });
  const { eligibilityChecks } = useEventTracking();

  const onRunCheck = useCallback(() => {
    eligibilityChecks.runCheckClicked({ patient_id: patientId, source });
    runCheck();
  }, [eligibilityChecks, patientId, runCheck, source]);

  return {
    reason: dueCheckReason(eligibilityCheckState, readiness, readinessLabel),
    isRunning,
    onRunCheck,
  };
}
